/* eslint-disable @typescript-eslint/no-extra-non-null-assertion */
import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { Fixture } from '@cds/betting-offer';
import { MarqueeIndexedFixtureView } from '@cds/betting-offer/domain-specific';
import { BetBuilderMarketInfo } from '@cds/betting-offer/domain-specific/bet-builder';
import { EventOption, EventOptionGroup, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { AutomatedMarqueeTilesConfig, MarqueeConfig } from '@frontend/sports/common/client-config-data-access';
import { AutomatedMarqueeTile, GeoLocationStatus, MarketType, MarqueeTile, MarqueeType } from '@frontend/sports/types/components/content';
import { MapMarketToGameTemplateIds, MarqueeGroupsOfMarkets } from '@frontend/sports/types/components/highlights-marquee';
import { isEqual } from 'lodash-es';

import { BetBuilderMarket } from '../betbuilder/model/bet-builder.model';
import { DetailedEventViewModel } from '../event-details-common/event-details-common.models';
import { OptionGroupService } from '../event-model/helpers/option-group.service';
import { AutomatedMarqueeImageService } from './automated-marquee-image.service';
import { MarqueeResponseMapperService } from './marquee-response-mapper.service';
import { BadgeAndRibbonVisibilityOptions, FixtureTile, NameVisibility, Tile, VisibilityOnMarquee } from './marquee-tile.model';

export enum AutomatedMarqueesOrder {
    Priceboost = 'Priceboost',
    BetBuilder = 'BuildABet',
    Regular = 'Regular',
}

@Injectable({ providedIn: 'root' })
export class AutomatedMarqueeService {
    private bgImages: string[] = [];
    private bgImageCount: number;

    constructor(
        private marqueeConfig: MarqueeConfig,
        private marqueeResponseMapperService: MarqueeResponseMapperService,
        private automatedMarqueeTilesConfig: AutomatedMarqueeTilesConfig,
        private automatedMarqueeImageService: AutomatedMarqueeImageService,
        private optionService: OptionGroupService,
    ) {}

    getTiles(fixture: Fixture, marqueeTiles: MarqueeTile[], betBuilderFixture?: Fixture, precreatedMarketsInfo?: BetBuilderMarketInfo[]): Tile[] {
        let newFixture: Fixture;
        const marqueeIndexedFixtureView: MarqueeIndexedFixtureView[] = [];
        marqueeTiles.forEach((tile, index) => {
            if (tile.marqueeType === MarqueeType.BuildABet && betBuilderFixture) {
                betBuilderFixture.addons = fixture.addons;
                marqueeIndexedFixtureView.push({
                    fixture: betBuilderFixture,
                    requestIndex: index,
                    precreatedMarketsInfo: precreatedMarketsInfo || [],
                });
            } else {
                newFixture = { ...fixture };
                newFixture.optionMarkets = [...[]];
                newFixture.games = [...[]];
                if (fixture.source === OfferSource.V1 && !fixture.hybridFixtureData?.fixtureId && fixture.games.length > 0) {
                    newFixture.games = [
                        ...fixture.games.filter((market) => market.id === (marqueeTiles[index]?.optionMarketId || marqueeTiles[index]?.gameId)),
                    ];
                } else {
                    newFixture.optionMarkets = [
                        ...fixture.optionMarkets.filter(
                            (market) => market.id === (marqueeTiles[index]?.gameId || marqueeTiles[index]?.optionMarketId),
                        ),
                    ];
                }

                if (tile.marketType === MarketType.SixPack) {
                    if (fixture.source === OfferSource.V1 && !fixture.hybridFixtureData?.fixtureId && fixture.games.length > 0) {
                        newFixture.games = [
                            ...fixture.games.filter((market) =>
                                market.grouping?.gridGroups?.find((x) => marqueeTiles[index]?.gridGroupIds?.includes(x)),
                            ),
                        ];
                    } else {
                        if (fixture.source === OfferSource.V1) {
                            newFixture.optionMarkets = [
                                ...fixture.optionMarkets.filter((market) =>
                                    market.grouping?.gridGroups?.find((x) => marqueeTiles[index]?.gridGroupIds?.includes(x)),
                                ),
                            ];
                        } else {
                            newFixture.optionMarkets = [
                                ...fixture.optionMarkets.filter((market) =>
                                    market.grouping?.gridGroups?.find((x) => marqueeTiles[index]?.gridGroupId?.includes(x)),
                                ),
                            ];
                        }
                    }
                }
                marqueeIndexedFixtureView.push({ fixture: newFixture, requestIndex: index, precreatedMarketsInfo: [] });
            }
        });

        return this.marqueeResponseMapperService.mapFixtures(marqueeIndexedFixtureView, marqueeTiles);
    }

    getCombinedTiles(marqueeTiles: Tile[], automatedMarqueeTiles: Tile[]) {
        this.bgImageCount = -1;
        const isImagesAvailable = this.bgImages?.length > 0;
        if (marqueeTiles.length === 0) {
            automatedMarqueeTiles.forEach((t) => {
                this.bgImageCount++;
                marqueeTiles.push({ ...this.mapImages(this.bgImageCount, isImagesAvailable, t) });
            });
        } else {
            automatedMarqueeTiles.forEach((tile) => {
                if (marqueeTiles.length < this.marqueeConfig.maxTiles) {
                    const templateOrGridGroupId =
                        (tile.source?.gridGroupIds?.length && tile.source?.gridGroupIds) ||
                        (tile.source?.gameTemplateIds?.length && tile.source?.gameTemplateIds);
                    if (tile.marqueeType === MarqueeType.Special) {
                        if (!marqueeTiles.some((x) => x.source?.optionId === tile.source?.optionId)) {
                            this.bgImageCount++;
                            marqueeTiles.push({ ...this.mapImages(this.bgImageCount, isImagesAvailable, tile) });
                        }
                    } else if (tile.marqueeType === MarqueeType.Regular) {
                        if (
                            !marqueeTiles.some(
                                (x) =>
                                    (x.source?.gridGroupIds?.length && isEqual(x.source?.gridGroupIds, templateOrGridGroupId)) ||
                                    (x.source?.gameTemplateIds?.length && isEqual(x.source?.gameTemplateIds, templateOrGridGroupId)) ||
                                    (!!(x as FixtureTile).optionGroup?.id &&
                                        (x as FixtureTile).optionGroup?.id === (tile as FixtureTile).optionGroup?.id),
                            )
                        ) {
                            this.bgImageCount++;
                            marqueeTiles.push({ ...this.mapImages(this.bgImageCount, isImagesAvailable, tile) });
                        }
                    } else {
                        if (
                            !marqueeTiles.some(
                                (x) => x.source?.optionMarketId === tile.source?.optionMarketId || x.source?.gameId === tile.source?.gameId,
                            )
                        ) {
                            this.bgImageCount++;
                            marqueeTiles.push({ ...this.mapImages(this.bgImageCount, isImagesAvailable, tile) });
                        }
                    }
                }
            });
        }

        return marqueeTiles;
    }

    private mapImages(count: number, isImagesAvailable: boolean, tile: Tile): Tile {
        const source = Object.assign({}, tile.source, {
            imageUrl: isImagesAvailable && tile.source ? this.bgImages[count % this.bgImages.length] : '',
        });
        tile.source = { ...source };
        (tile as FixtureTile).imageUrl = source?.imageUrl;

        return tile;
    }

    createAutomatedMarqueeTiles(model: DetailedEventViewModel): MarqueeTile[] {
        if (!this.marqueeConfig.isAutomatedMarqueesEnabled) {
            return [];
        }

        const marqueeTiles: MarqueeTile[] = [];

        this.bgImages = this.automatedMarqueeImageService.getImages(model.model?.sport.id!, model.model?.league.id!);

        this.marqueeConfig.automatedMarqueesOrder.forEach((order) => {
            if (marqueeTiles.length < this.marqueeConfig.maxTiles) {
                switch (order) {
                    case AutomatedMarqueesOrder.Priceboost:
                        if (model.model?.priceBoostCount) {
                            marqueeTiles.push(...this.getPriceBoostTiles(model, this.marqueeConfig.maxTiles - marqueeTiles.length));
                        }
                        break;
                    case AutomatedMarqueesOrder.BetBuilder:
                        if (model.preCreated?.promotedMarkets?.length) {
                            marqueeTiles.push(...this.getBABTiles(model, this.marqueeConfig.maxTiles - marqueeTiles.length)!);
                        }
                        break;
                    case AutomatedMarqueesOrder.Regular:
                        marqueeTiles.push(...this.getRegularTiles(model, this.marqueeConfig.maxTiles - marqueeTiles.length));
                        break;
                }
            }
        });

        return marqueeTiles;
    }

    private getBABTiles(model: DetailedEventViewModel, maxTilesCount: number) {
        const that = this;
        const betBuilderMarkets = model?.preCreated?.hasMarkets
            ? [
                  ...(model?.preCreated?.promotedMarkets || []),
                  ...(model?.preCreated?.ambassadorsMarkets || []),
                  ...(model?.preCreated?.themedTabMarkets || []),
              ]
            : [];

        return betBuilderMarkets
            .reduce(function (marqueeTiles: MarqueeTile[], betbuilderMarket) {
                marqueeTiles.push(that.creatAutomatedTile(MarqueeType.BuildABet, model, undefined, undefined, undefined, betbuilderMarket));

                return marqueeTiles;
            }, [])
            .slice(0, maxTilesCount);
    }

    private creatAutomatedTile(
        marqueeType: MarqueeType,
        model: DetailedEventViewModel,
        marketType?: MarketType,
        optionGroup?: EventOptionGroup,
        option?: EventOption,
        betBuilderMarket?: BetBuilderMarket,
    ): MarqueeTile {
        const source = model.model?.offerSource;
        const parameters = optionGroup?.parameters;
        const tileColorInfo = this.getColorProperties(marqueeType);

        return {
            sportId: model.model?.sport.id!,
            showOnEventPage: true,
            showOnCompetitionPage: false,
            showOnHomeLobby: false,
            showOnTeamsPage: false,
            showOnEsportsLandingPage: false,
            showOnMultiSportsLobby: false,
            crmPromotionVisibility: VisibilityOnMarquee.MarqueeOnly,
            marqueeType,
            isPriceBoostMarquee: option ? true : false,
            gridGroupIds: this.getGridGroupIds(marketType!, model.model?.sport.id!),
            gridGroupId: '',
            eventIds: [model.model?.id!],
            hideBuildABetBadge: false,
            geoLocationStatus: GeoLocationStatus.Allowed,
            regionId: model.model?.region.id!,
            regionIds: [],
            optionId: option?.id,
            betbuilderSelectionOverride: option ? BadgeAndRibbonVisibilityOptions.ShowBadgeWithoutTitleOverride : '',
            badgeTitleOverride: marqueeType === MarqueeType.Special ? tileColorInfo?.badgeTitleOverride : '',
            badgeOverrideText: marqueeType === MarqueeType.BuildABet ? tileColorInfo?.badgeTitleOverride : '',
            badgeAndRibbonVisibilityOption: marqueeType === MarqueeType.Special ? BadgeAndRibbonVisibilityOptions.ShowBadgeWithTitleOverride : '',
            awayIndicatorOverride: '',
            eventNameOverride: '',
            eventNameVisibility: '',
            fallbackMarketType: undefined,
            gameId:
                model?.preCreated?.hasMarkets && betBuilderMarket
                    ? betBuilderMarket?.id
                    : source === OfferSource.V1
                      ? +optionGroup?.originalId!
                      : undefined,
            optionMarketId:
                model?.preCreated?.hasMarkets && betBuilderMarket
                    ? betBuilderMarket?.id
                    : source === OfferSource.V2
                      ? +optionGroup?.originalId!
                      : undefined,
            textColor: tileColorInfo?.textColor,
            backgroundColor: '',
            trafficId: '',
            resultId: undefined,
            competitionId: model.model?.league.id!,
            competitionIds: [model.model?.league.id!],
            fixtureId: model.model?.id!,
            imageAlt: '',
            imageUrl: '',
            gameTemplateIds: source === OfferSource.V1 ? this.getGameTemplateIds(marketType!, model?.model?.sport.id!) : [],
            crmPromotionId: '',
            leftAlignOddsButton: false,
            marketAndOptionNameOverride: source === OfferSource.BetBuilder ? betBuilderMarket?.name! : '',
            marketAndOptionNameVisibility: source === OfferSource.BetBuilder ? NameVisibility.WithOverride : '',
            noEventSportIds: [],
            optionMarketParameters: {
                happening: parameters?.find((p) => p.key === Tv2MarketParameters.Happening)?.value
                    ? [parameters?.find((p) => p.key === Tv2MarketParameters.Happening)!.value]
                    : [],
                marketTypes: parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value
                    ? [parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)!.value]
                    : [],
                period: parameters?.find((p) => p.key === Tv2MarketParameters.Period)?.value
                    ? [parameters?.find((p) => p.key === Tv2MarketParameters.Period)!.value]
                    : [],
                position: [],
            },
            overriddenTeamNames: '',
            showOnSportsLobby: false,
            trackingDetail: {
                marqueeName: 'AutomatedMarquee',
                marqueeRecommendationType: '',
                moduleCustomName: '',
                modulePosition: '',
                pageLayout: '',
                sitecoreTemplateId: '',
            },
            link: { href: '', link: '', popup: '', target: '' },
            marketType: marketType!,
            isAutomatedMarquee: true,
            optionGroupId: '',
            doNotShowAnyBadgeRibbon: false,
        };
    }

    private getGameTemplateIds(marketType: MarketType, sportId: number): number[] {
        const gameTemplateIds = this.marqueeConfig.mapMarketToGameTemplateIds.find(
            (market) => market.sportId === sportId && (market.market as MarketType) === marketType,
        );

        return gameTemplateIds?.gameTemplateIds ? gameTemplateIds?.gameTemplateIds : [];
    }

    private getGridGroupIds(marketType: MarketType, sportId: number): string[] {
        const marqueeGroupOfmarkets = this.marqueeConfig.marqueeGroupsOfMarkets.find(
            (market) => market.sportId === sportId && (market.groupOfMarkets as MarketType) === marketType,
        );

        return marqueeGroupOfmarkets ? marqueeGroupOfmarkets.gridGroupId.split(',') : [];
    }

    private getColorProperties(marqueeType: MarqueeType): AutomatedMarqueeTile {
        return this.automatedMarqueeTilesConfig.automatedMarqueesTiles.find((x) => (x.marqueeType as MarqueeType) === marqueeType)!;
    }

    private getRegularTiles(model: DetailedEventViewModel, maxTiles: number): MarqueeTile[] {
        const tiles: MarqueeTile[] = [];

        const marqueeGroupOfMarkets = this.marqueeConfig.marqueeGroupsOfMarkets.filter((x) => x.sportId === model.model?.sport.id);
        const mapMarketToGameTemplateIds = this.marqueeConfig.mapMarketToGameTemplateIds.filter((x) => x.sportId === model.model?.sport.id);

        if (model.model?.offerSource === OfferSource.V1) {
            tiles.push(...this.getTV1TilesByGameTemplateId(mapMarketToGameTemplateIds, model, maxTiles));
        } else {
            tiles.push(...this.getTV2TilesByMarketType(mapMarketToGameTemplateIds, model, maxTiles));
        }

        tiles.push(...this.getTilesByGroupId(marqueeGroupOfMarkets, model, maxTiles));

        const order = this.marqueeConfig.automatedRegularMarqueesOrder[model.model?.sport.id || 0] || [];

        const tiles_sorted: MarqueeTile[] = [];
        order.forEach((marketType) => {
            const tile = tiles.filter((obj) => {
                return obj.marketType === marketType;
            });
            if (tile.length > 0 && tiles_sorted.length < maxTiles) {
                tiles_sorted.push(...tile);
            }
        });

        return tiles_sorted;
    }

    private getPriceBoostTiles(model: DetailedEventViewModel, numOfTiles: number): MarqueeTile[] {
        const marqueeTiles: MarqueeTile[] = [];

        model.model?.optionGroups.forEach((x) => {
            if (x.visible) {
                const options = x.options.filter((option) => option.boostedPrice && option.visible);
                options.forEach((y) => {
                    if (!marqueeTiles.some((t) => t.optionId === y.id) && marqueeTiles.length <= numOfTiles) {
                        marqueeTiles.push(this.creatAutomatedTile(MarqueeType.Special, model, undefined, x, y));
                    }
                });
            }
        });

        return marqueeTiles;
    }

    private getTilesByGroupId(marqueeGroupOfMarkets: MarqueeGroupsOfMarkets[], model: DetailedEventViewModel, numOfTiles: number): MarqueeTile[] {
        const tile: MarqueeTile[] = [];
        marqueeGroupOfMarkets.forEach((x) => {
            const gridGroupIds = x.gridGroupId.split(',');
            if (gridGroupIds.length === 3 || gridGroupIds.length === 4) {
                if (gridGroupIds.every((id) => model.model?.optionGroups?.some((group) => group.gridGroups?.includes(id)))) {
                    tile.push(this.creatAutomatedTile(MarqueeType.Regular, model, MarketType.SixPack));
                }
            } else {
                const options = model.model?.optionGroups?.filter(
                    (y) => x.gridGroupId.split(',').every((id) => y.gridGroups?.includes(id)) && y.visible,
                )!;
                const optionGroup = this.optionService.get(options, x.gridGroupId);
                if (optionGroup && tile.length < numOfTiles) {
                    if (tile.some((t) => t.marketType === MarketType.SixPack)) {
                        const sixPacktiles = tile.filter((t) => t.marketType === MarketType.SixPack);
                        if (sixPacktiles?.length && sixPacktiles.some((s) => s.gridGroupIds?.some((g) => optionGroup.gridGroups?.includes(g)))) {
                            return;
                        }

                        return;
                    } else {
                        tile.push(this.creatAutomatedTile(MarqueeType.Regular, model, x.groupOfMarkets as MarketType, optionGroup));
                    }
                }
            }
        });

        return tile;
    }

    private getTV1TilesByGameTemplateId(
        mapMarketToGameTemplateIds: MapMarketToGameTemplateIds[],
        model: DetailedEventViewModel,
        numOfTiles: number,
    ): MarqueeTile[] {
        const tiles: MarqueeTile[] = [];
        mapMarketToGameTemplateIds.forEach((x) => {
            const options = model.model?.optionGroups?.filter((y) => x.gameTemplateIds?.includes(+y.templateId) && y.visible);
            if (options?.length) {
                tiles.push(this.creatAutomatedTile(MarqueeType.Regular, model, x.market as MarketType, options[0]));
            }
        });

        return tiles.slice(0, numOfTiles);
    }

    private getTV2TilesByMarketType(mapMarketToGameTemplateIds: MapMarketToGameTemplateIds[], model: DetailedEventViewModel, numOfTiles: number) {
        const tile: MarqueeTile[] = [];
        let isTileCreated = false;
        mapMarketToGameTemplateIds.forEach((x) => {
            isTileCreated = false;
            const optionGroups = model.model?.optionGroups?.filter(
                (y) =>
                    y.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value &&
                    y.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === x?.marketType &&
                    y.visible,
            );

            optionGroups?.forEach((o) => {
                if (!tile.some((t) => t.optionMarketId === +o.originalId || t.marketType === x.market) && tile.length < numOfTiles) {
                    if (
                        x.marketFilterParams &&
                        x.marketFilterParams?.every((param) => o.parameters?.find((p) => p.key === param.key)?.value === param.value)
                    ) {
                        tile.push(this.creatAutomatedTile(MarqueeType.Regular, model, x.market as MarketType, o));
                        isTileCreated = true;
                    } else if (
                        x.position &&
                        !tile.some((t) => t.optionMarketId === +o.originalId) &&
                        model?.fixture?.optionMarkets
                            .find((market) => market.id === +o.originalId)
                            ?.parameters.find((p) => p.key === Tv2MarketParameters.Position)?.value === x.position
                    ) {
                        tile.push(this.creatAutomatedTile(MarqueeType.Regular, model, x.market as MarketType, o));
                        isTileCreated = true;
                    } else if (
                        x.happening &&
                        o.parameters?.find((p) => p.key === Tv2MarketParameters.Happening)?.value === x.happening &&
                        !tile.some((t) => t.optionMarketId === +o.originalId)
                    ) {
                        tile.push(this.creatAutomatedTile(MarqueeType.Regular, model, x.market as MarketType, o));
                        isTileCreated = true;
                    }
                }
            });

            if (!isTileCreated && optionGroups?.length && !x.position && !x.marketFilterParams && !x.happening) {
                tile.push(this.creatAutomatedTile(MarqueeType.Regular, model, x.market as MarketType, optionGroups[0]));
            }
        });

        return tile;
    }
}
